// cleanup_convert_to_typescript_file_SECURITY_QUESTIONS
// cleanup_remove_external_libraries_SECURITY_QUESTIONS

import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { isEqual } from 'lodash';
import { useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { forkJoin, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import PropTypes from 'prop-types';

import { FormSection } from '@glu/validation-react';
import { Button, PRIMARY, SECONDARY } from '@glu/buttons-react';
import Alert from '@glu/alerts-react';

import Workflow from 'components/workflow';
import useLocale from 'components/useLocale';
import SecurityQuestionsForm from 'components/firstTimeLoginFlow/SecurityQuestionsForm';
import { unsubscribeComponent } from 'components/utils/util';
import {
  getAllQuestions$,
  getSecurityQuestionsSecured$,
  saveAnswers$
} from 'api/securityQuestions/service';
import { fetchUserData } from 'api/user/service';
import { setSnackBar } from 'logic/store/snackbar/actions';
import { clearGrids } from 'logic/store/grids/actions';
import {
  logout as logoutAction,
  setCurrentUser as setCurrentUserAction
} from 'logic/store/authentication/actions';
import { ALERTVARIATIONS, RESPONSES } from 'constants.js';

import SecurityQuestionsProvider from './SecurityQuestionsProvider';
import useConfirmation from '../useConfirmation';

import useStyles from './UpdateSecurityQuestions.styles';

const subscriptions = {
  saveAnswers: null,
  loadData: null
};

const { SUCCESS } = RESPONSES;

const UpdateSecurityQuestions = ({ page }) => {
  const { animatingButton, intro, loaded, marginRight, wrapperStyles } = useStyles({ page });
  const locale = useLocale();
  const { state: { previous } = {} } = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const setSnackBarMessage = ({ message, type, uid }) =>
    dispatch(setSnackBar({ uid, message, type }));
  const setCurrentUser = (user) => dispatch(setCurrentUserAction(user));
  const [loadedTheme, setLoadedTheme] = useState(false);
  const [APIData, setAPIData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [Confirmation, setConfirmation] = useConfirmation();
  const logout = () => {
    setConfirmation(() => ({
      execute: of(1).pipe(
        tap(() => {
          clearGrids(dispatch);
          return dispatch(logoutAction());
        })
      ),
      words: {
        title: locale['common.logout'],
        action: locale['header.logoff'],
        presentTenseAction: locale['common.loggingOut']
      }
    }));
  };
  const submit = async ({ validate }) => {
    const {
      data: { security },
      isValid
    } = await validate();
    // TODO: scroll to error
    if (isValid) {
      setIsSubmitting(true);
      subscriptions.saveAnswers = saveAnswers$({
        answer1: security.securityQuestionAnswer1,
        answer2: security.securityQuestionAnswer2,
        answer3: security.securityQuestionAnswer3,
        question1Id: security?.securityQuestion1?.[0]?.id,
        question2Id: security?.securityQuestion2?.[0]?.id,
        question3Id: security?.securityQuestion3?.[0]?.id
      }).subscribe(async ({ data, success }) => {
        setIsSubmitting(false);
        if (!success) {
          setAlertMessage(data?.[0]?.userMessage || locale['common.server.error']);
        } else {
          setAPIData((state) => ({ ...state, initialData: { security } }));
          setSnackBarMessage({
            uid: new Date().getTime(),
            message: locale['common.answersSaved'],
            type: SUCCESS
          });
          const currentUser = await fetchUserData();
          setCurrentUser(currentUser);
        }
      });
    }
  };

  const showAPIError = (data) => {
    setAPIData((state) => ({ ...state, APIError: true }));
    setAlertMessage(data?.[0]?.userMessage || locale['common.server.error']);
  };
  // Load data and state
  useEffect(() => {
    setLoadedTheme(true);
    subscriptions.loadData = forkJoin({
      questions: getAllQuestions$(),
      answeredQuestions: page ? getSecurityQuestionsSecured$() : of(false)
    }).subscribe(({ answeredQuestions, questions: { data, success } }) => {
      if (!success) showAPIError(data);
      const questions = data.securityQuestions
        ? data.securityQuestions.map((question) => ({
            ...question,
            id: question.questionId,
            name: question.question
          }))
        : null;
      const formattedAnsweredQuestions = answeredQuestions?.success
        ? (answeredQuestions?.data?.securityQuestions || []).map((question) => ({
            ...question,
            id: question.questionId,
            name: question.question
          }))
        : [];
      const initialData = {
        security: {
          ...(formattedAnsweredQuestions.length > 0
            ? {
                securityQuestion1: [formattedAnsweredQuestions[0]],
                securityQuestion2: [formattedAnsweredQuestions[1]],
                securityQuestion3: [formattedAnsweredQuestions[2]]
              }
            : {})
        }
      };
      setAPIData((state) => ({ ...state, questions, initialData }));
    }, showAPIError);
    return () => {
      unsubscribeComponent(subscriptions);
      setAlertMessage(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // eslint-disable-next-line no-nested-ternary
  let title = APIData
    ? page
      ? locale['common.changeSecurityQuestions']
      : locale['profile.setup']
    : locale['common.loading'];
  if (APIData && APIData.APIError) {
    title = `${page ? locale['common.changeSecurityQuestions'] : locale['profile.setup']} - ${
      locale['common.loadingFailed']
    }`;
  }

  return (
    <Workflow
      title={title}
      returnUrl={page ? previous || '/' : false}
      className={classnames({ [intro]: !page, [loaded]: loadedTheme })}
    >
      {!APIData ? (
        <div />
      ) : (
        <SecurityQuestionsProvider initialData={APIData.initialData}>
          {({ formContext: { onChange, validate }, values }) => {
            const securityValues = values.security || {};
            // Only allow the user to use question once
            const availableQuestionsToPickFrom =
              APIData && APIData.questions
                ? APIData.questions.filter((question) => {
                    const selectedQuestions = [
                      securityValues?.securityQuestion1?.[0]?.id,
                      securityValues?.securityQuestion2?.[0]?.id,
                      securityValues?.securityQuestion3?.[0]?.id
                    ];
                    return !selectedQuestions.includes(question.id);
                  })
                : [];
            const hasUnsavedChanges = !isEqual(APIData?.initialData?.security, values.security);
            return (
              <div className={wrapperStyles}>
                <Confirmation />
                {alertMessage && (
                  <Alert
                    dataQa="action-error"
                    variant={ALERTVARIATIONS.ERROR}
                    message={alertMessage}
                  />
                )}
                {APIData && (
                  <form>
                    <FormSection name="security">
                      <SecurityQuestionsForm
                        availableQuestionsToPickFrom={availableQuestionsToPickFrom}
                        onChange={onChange}
                        values={values}
                      />
                    </FormSection>
                    <br />
                    {/* If user is entering from sidebar link/page otherwise they are coming from getUserSession response isSecurityAnsNeeded: true */}
                    {page ? (
                      <>
                        {hasUnsavedChanges && (
                          <Button
                            variant={PRIMARY}
                            onClick={() => submit({ validate })}
                            className={classnames(animatingButton, marginRight, {
                              animating: isSubmitting
                            })}
                            data-qa="updateSecurityQuestions"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? locale['common.saving'] : locale['common.save']}
                          </Button>
                        )}
                        <Button
                          variant={SECONDARY}
                          onClick={() => history.push(previous || '/')}
                          data-qa="back"
                        >
                          {hasUnsavedChanges ? locale['common.cancel'] : locale['common.back']}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant={PRIMARY}
                          onClick={() => submit({ validate })}
                          className={classnames(animatingButton, marginRight, {
                            animating: isSubmitting
                          })}
                          data-qa="updateSecurityQuestions"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? locale['common.saving'] : locale['common.save']}
                        </Button>
                        <Button variant={SECONDARY} onClick={logout} data-qa="cancel">
                          {locale['common.cancel']}
                        </Button>
                      </>
                    )}
                  </form>
                )}
              </div>
            );
          }}
        </SecurityQuestionsProvider>
      )}
    </Workflow>
  );
};

UpdateSecurityQuestions.propTypes = {
  page: PropTypes.bool
};

UpdateSecurityQuestions.defaultProps = {
  page: false
};

export default UpdateSecurityQuestions;
